import { useState, useCallback } from 'react';
import { apiFetch } from '../../../config/api';
import { debugError } from '../../../utils/debugLog';

/**
 * Custom hook to fetch detailed template analytics
 *
 * @returns {Object} Hook interface with loading, error, data, and fetch function
 */
export const useTemplateAnalyticsDetail = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const fetchAnalytics = useCallback(async templateId => {
    if (!templateId) {
      setError('Template ID is required');
      return null;
    }

    setLoading(true);
    setError(null);
    setData(null);

    try {
      const response = await apiFetch(
        `/api/v1/templates/analytics/templates/${templateId}`
      );

      if (response && response.success && response.template) {
        setData(response.template);
        return response.template;
      } else {
        throw new Error(
          response?.message || 'Failed to fetch template analytics'
        );
      }
    } catch (err) {
      const errorMessage =
        err.message || 'An error occurred while fetching analytics';
      setError(errorMessage);
      debugError('Error fetching template analytics:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const clearData = useCallback(() => {
    setData(null);
  }, []);

  return {
    loading,
    error,
    data,
    fetchAnalytics,
    clearError,
    clearData,
  };
};

export default useTemplateAnalyticsDetail;
